import { isBrowser } from '@utils/isBrowser';
import classNames from 'classnames';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import Portal from '../Portal';

type Position = 'above' | 'below';
type Props = {
  selectionRef: RefObject<HTMLElement>;
  parentRef: RefObject<HTMLElement>;
  onShow?: () => void;
  onHide?: () => void;
  children?: React.ReactNode;
};

const MAX_DISTANCE_FROM_TOP = 200;
const OFFSET = 10;

const Tooltip: React.FC<Props> = ({
  children,
  onHide,
  onShow,
  parentRef,
  selectionRef,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>('above');

  const hide = useCallback(() => {
    if (onHide) onHide();

    setVisible(false);
  }, []);

  const show = useCallback(() => {
    if (onShow) onShow();
    if (selectionRef.current === null) return;

    setVisible(true);
    setPosition(
      selectionRef.current.getBoundingClientRect().top > MAX_DISTANCE_FROM_TOP
        ? 'above'
        : 'below',
    );
  }, [selectionRef]);

  useEffect(() => {
    if (selectionRef.current === null) return;

    selectionRef.current.addEventListener('mouseenter', show);
    selectionRef.current.addEventListener('mouseleave', hide);
    selectionRef.current.addEventListener('click', show);
    selectionRef.current.addEventListener('blur', hide);
    selectionRef.current.addEventListener('focus', show);
    window.addEventListener('scroll', hide, { passive: true });

    return () => {
      selectionRef.current?.removeEventListener('mouseenter', show);
      selectionRef.current?.removeEventListener('mouseleave', hide);
      selectionRef.current?.removeEventListener('click', show);
      selectionRef.current?.removeEventListener('blur', hide);
      selectionRef.current?.removeEventListener('focus', show);
      window.removeEventListener('scroll', hide);
    };
  }, [selectionRef, onShow, onHide]);

  if (!isBrowser()) return null;
  if (selectionRef.current === null) return null;
  if (parentRef.current === null) return null;

  const parentRect = parentRef.current.getBoundingClientRect();
  const width = tooltipRef.current?.clientWidth ?? 0;
  const height = tooltipRef.current?.clientHeight ?? 0;

  return (
    <Portal>
      <div
        ref={tooltipRef}
        className={classNames(
          'pointer-events-none fixed z-90',
          'bg-neutral-6 text-center',
          'max-w-44 rounded-sm px-2 py-2 transition duration-300 ease-out',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
        style={{
          left: parentRect.left - width / 2 + parentRect.width / 2,
          top:
            position === 'above'
              ? parentRect.top - height - OFFSET
              : parentRect.top + parentRect.height + OFFSET,
        }}
      >
        <div
          className={classNames(
            'absolute left-1/2 -ml-1 h-0 w-0 border-4 border-neutral-6',
            {
              '-bottom-1 border-b-0': position === 'above',
              '-top-1 border-t-0': position === 'below',
            },
          )}
          style={{
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
          }}
        />
        <p className="text-xs text-white">{children}</p>
      </div>
    </Portal>
  );
};

export default Tooltip;
